import {
  requestCreator,
  RequestCreator,
  PaymentRequestCreator,
  makeUrl,
  CONFIGURABLE_API_ENDPOINT,
  CONFIGURABLE_PAY_API_ENDPOINT,
} from 'lib/apiHelpers';
import { call, put } from 'redux-saga/effects';
import { signInAction, signOutAction, expiredAction } from 'domain/env';
import { notify } from 'lib/errorLogger';
import * as c from './contracts';
import tokenKeeper from 'lib/apiTokenKeeper';
import { jeResponseModifier } from './journal/responseModifier';


const SAGA_SYNC_ACTION_FAILURE = 'SAGA/SYNC_ACTION/FAILURE';

export function logoutAction(action, err) {
  // @to-do catch network error by its type until it reaches
  // this line and be sure err.response is present

  if (
    action.type !== signInAction.type &&
    err.response &&
    err.response.status &&
    err.response.status.toString() === process.env.REACT_APP_UNAUTHORIZED_CODE
  ) {
    return {
      type: signOutAction.type,
    };
  }

  if (action.type === signInAction.type && err.response && !err.response.status) {
    const fakeError = { response: { data: 'Something went wrong. Please, refresh the page or contact support' } };

    return {
      type: action.failure,
      err: fakeError,
    };
  }

  if (
    err.response &&
    err.response.status &&
    err.response.status.toString() === process.env.REACT_APP_TRIAL_EXPIRED_CODE
  ) {
    return {
      type: expiredAction.type,
    };
  }

  return {
    type: action.failure || SAGA_SYNC_ACTION_FAILURE,
    err,
  };
}

export function* doLogout(action, err) {
  yield put(logoutAction(action, err));
}

export function ensure(api, action, { adapter = (d) => d, onSuccess, onFailure } = {}) {
  return function* requestApi(args = {}) {
    yield put({ type: action.request });
    try {
      const resp = yield call(api, args);
      yield put({
        ...args,
        type: action.success,
        payload: adapter(resp),
      });
      if (typeof onSuccess === 'function') onSuccess(resp);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('error in ensure', err);
      yield doLogout(action, err);
      if (typeof onFailure === 'function') onFailure(err);
    }
  };
}

const API_ROOT_FULL = `${CONFIGURABLE_API_ENDPOINT || ''}/v2/3/` || '';
const PAY_API_ROOT_FULL = `${CONFIGURABLE_PAY_API_ENDPOINT || ''}/v2/3/pay/` || '';

const publickRequest = requestCreator(API_ROOT_FULL);
const request = requestCreator(API_ROOT_FULL, tokenKeeper);
const uploadRequest = requestCreator(API_ROOT_FULL, tokenKeeper);

function errorLoger(error, meta) {
  notify.captureEvent(error, { data: meta }); // TODO: need create tage for separate error type
}

const rcInstance = new RequestCreator(API_ROOT_FULL, errorLoger, tokenKeeper);
const rcPaymentInstance = new PaymentRequestCreator(PAY_API_ROOT_FULL, errorLoger, tokenKeeper);

export default {
  getDevices: rcInstance.get(makeUrl`getSigners/${'id'}`, c.validateDeviceList),
  getHashDevices: rcInstance.get('getHashDevices', (v) => v),
  connectHashDevice: rcInstance.post('hash/authorization', (v) => v),
  saveHashCompanyId: request.post('hash/save_company_id'),
  signIn: publickRequest.post('loginUser', c.validateUserCredentials),
  checkTwoFACode: publickRequest.post('checkTwoFACode', (x) => x),
  resendTwoFACode: publickRequest.post('resendTwoFACode', (x) => x),
  signInSage: request.post('sage_sa/authorization'),
  companyLogin: request.get('companyLogin', (x) => x),
  saveSageCompany: request.post('sage_sa/save_company_id'),
  signInPriority: request.post('priority_api/authorization'),
  signInSAP: request.post('sap_api/authorization'),
  signInNetSuite: request.post('netsuite_api/authorization'),
  signInMDNavision: request.post('microsoft_dynamics_nav/authorization'),
  saveMDNavisionCompany: request.post('microsoft_dynamics_nav/save_company_id'),
  signInMDBC: request.post('microsoft_dynamics_bc/authorization'),
  saveMDBCCompany: request.post('microsoft_dynamics_bc/save_company_id'),
  signInAcumatica: request.post('acumatica_api/authorization'),
  signInAcumaticaAdvanced: request.post('acumatica_advanced_api/authorization'),
  signInHashWeb: request.post('hash_web/authorization'),
  // signInNetSuit: rcInstance.mock({}, x => x),
  savePriorityCompany: request.post('priority_api/save_company_id'),
  signInSAP4Hana: request.post('sap4hana_api/authorization'),
  saveSAP4Hana: request.post('sap4hana_api/save_company_id'),
  getZohoCompanyList: rcInstance.get('zoho/organization', (v) => v),
  setZohoCompany: rcInstance.post('zoho/organization', (v) => v),
  getCompanies: rcInstance.get('getListOfCompanies', c.validateCompanyList),
  getCompaniesGrid: rcInstance.get('grid/companies', (v) => v),
  markCompanyPinned: request.get('getListOfCompanies'), // @todo fix url
  getListRecent: request.get('listRecent'),
  getDocumentsExportFormats: request.get('exportFormats'),
  documentSearch: rcInstance.get('search', c.validateSearch),
  documentSearchByCategory: rcInstance.get('search_new', c.validateSearch),
  documentSearchBySupplier: rcInstance.get('supplier_view/search', c.validateSearch),
  searchInCompanies: rcInstance.get('search_in_companies', (x) => x),
  getDocument: rcInstance.get('getDocumentMetadata', c.validateDocumentMetadata),
  bulkCreateDocumentPayment: rcInstance.post('payments/bulk', (x) => x),
  getOptionsList: rcInstance.get('getOptionsList', (x) => x),
  removeDocument: request.post('removeDocument'),
  updateDocumentTags: rcInstance.post('updateTags', c.validateUpdateTags),
  updateMultipleDocumentsTags: rcInstance.post('updateTagsDocuments/', (x) => x),
  changeTypeDocuments: rcInstance.post('changeTypeDocuments', c.validateDocumentsBulkUpdateResponse),
  changeTypeDocument: rcInstance.post('changeTypeDocument', c.validateDocumentMetadata),
  linkDocument: request.post('linkDocument'),
  linkDocuments: request.post('linkDocuments'),
  lockLinkedDocuments: request.post('updateLinkState'),
  unlinkDocument: request.post('unlinkDocument'),
  unlinkMultiDocuments: request.post('unlinkMultiDocuments'),
  updateLink: request.post('updateLink'),
  updateNotes: rcInstance.post('updateNotes', c.validateUpdateNotes),
  removeNotes: rcInstance.post('removeNotes', c.validateRemoveNotes),
  uploadDocument: uploadRequest.post('uploadDocument'),
  uploadSupplierDocument: uploadRequest.post('supplier_view/uploadDocuments'),
  rejectDocument: request.post('rejectDocument'),
  duplicateAsMultiGrv: request.post('priority/duplicate-as-multi-grv'),
  getDashboardStatus: rcInstance.get('getDashboardStats', c.validateDashboardState),
  getStatementTxnSearch: request.post('statementTxnSearch', c.validateGoogleSearch),
  // TODO uncomment after fix performance
  // getJournalEntry: rcInstance.get('getJournalEntry', c.validateJournalEntry),
  getJournalEntry: rcInstance.get('getJournalEntry', (x) => x, jeResponseModifier),

  getJEList: rcInstance.get('getJournalEntryList', (x) => x),
  updateJournalEntry: rcInstance.post('updateJournalEntry', (x) => x),
  updateJournalEntryFromInsights: rcInstance.post('updateJournalEntryFromInsights', (x) => x),
  getTextMap: request.get('getTextMap'),
  getSettings: rcInstance.get('getCompanyPreferences', c.validateCompanyPreferences),
  getSlackConnection: rcInstance.get('slack/connection/status', (x) => x),
  disconnectSlack: rcInstance.post('slack/connection/revoke', (x) => x),
  connectSlack: rcInstance.get('slack/install', (x) => x),
  getSSOParams: request.get('getSSOParams'),
  disconnectErp: request.get('disconnectErp'),
  updateFavoriteCompanies: request.post('updateFavoriteCompanies'),
  acceptDocument: rcInstance.get('acceptDocument', c.validateAcceptDocument),
  acceptDocumentAsBackgroundTask: rcInstance.post(makeUrl`documents/${'documentID'}/accept`, (x) => x),
  syncDocumentAsBackgroundTask: rcInstance.post(makeUrl`documents/${'documentID'}/sync`, (x) => x),
  checkActivate: publickRequest.post('activateUser'),
  updatePrefs: request.post('updatePrefs'),
  splitDocument: request.post('splitDocument'),
  unlinkDocuments: request.post('unlinkDocuments'),
  getCompanyChatContext: rcInstance.get('getChatPeers', c.validateChatPeers),
  getChatUnreadCounts: rcInstance.get('sendbird/unread', (v) => v),
  getUserProfile: rcInstance.get('getUserProfile', (x) => x),
  getMRUTags: request.get('getMRUTags'),
  signDocument: request.post('signDocument'),
  signDocuments: rcInstance.post('signDocuments', c.validateDocumentsBulkUpdateResponse),
  bulkAcceptDocuments: rcInstance.post('bulkAcceptDocument', (x) => x),
  bulkApprove: rcInstance.post('approvals/bulkSignDocuments', (x) => x),
  bulkSetApprovals: rcInstance.post('bulkSetApprovalFlowNew', (x) => x),
  moveDocumentToCompany: rcInstance.post('moveDocumentToCompany', (x) => x),
  moveDocumentsToCompany: rcInstance.post('moveDocumentsToCompany', (x) => x),
  mergeDocuments: rcInstance.post('mergeDocuments', c.validateDocumentsBulkUpdateResponse),
  mergeDocumentsForPrinting: rcInstance.post('getMergedDocuments', (x) => x),
  getOrganization: rcInstance.get(makeUrl`organizations/${'id'}`, c.validateOrganizations),
  updateOrganization: rcInstance.post(makeUrl`organizations/${'id'}`, c.validateUpdateOrganization),
  updateOrganizationLogo: rcInstance.post(makeUrl`organizations/${'id'}/uploadLogo`, c.validateEmptyContracts),
  createOrganization: rcInstance.post('organizations', c.validateCreateOrganization),
  getPasswordExpiration: rcInstance.get(makeUrl`preferences/org/${'id'}/password_expiration`, (x) => x),
  updatePasswordExpiration: rcInstance.post(makeUrl`preferences/org/${'id'}/password_expiration`, (x) => x),
  createCompany: rcInstance.post('company', c.validateCreateCompany),
  getAllCompanies: rcInstance.get('company', (x) => x),
  exportData: rcInstance.url('newExportData'),
  getSignings: rcInstance.get(makeUrl`preferences/${'type'}/${'id'}/signing`, c.validateGetSignings),
  updateSignings: rcInstance.put(makeUrl`preferences/${'type'}/${'id'}/signing`, c.validateUpdateSignings),
  removeSignings: rcInstance.delete(makeUrl`preferences/${'type'}/${'id'}/signing`, c.validateRemoveSignings),
  setCompanyFeatures: rcInstance.post(makeUrl`preferences/company/${'id'}/feature_set`, c.validateSetCompanyFeatures),
  getCompanyFeatures: rcInstance.get(makeUrl`preferences/company/${'id'}/feature_set`, c.validateSetCompanyFeatures),
  getFTPConnection: rcInstance.get(makeUrl`preferences/company/${'id'}/ftp_connection`, (x) => x),
  saveFTPConnection: rcInstance.post(makeUrl`preferences/company/${'id'}/ftp_connection`, (x) => x),
  getCompanyConnections: rcInstance.get(
    makeUrl`preferences/company/${'id'}/connections`,
    c.validateGetCompanyConnections,
  ),
  getCompanyFeaturesLayout: rcInstance.get(makeUrl`preferences/company/${'id'}/layout/features`, (x) => x),
  getCompanyErpLayout: rcInstance.get(makeUrl`preferences/company/${'id'}/layout/erp`, (x) => x),
  getCompanyApprovalFeaturesLayout: rcInstance.get(
    makeUrl`preferences/company/${'id'}/layout/approval-features`,
    (x) => x,
  ),
  syncErpIndexes: rcInstance.post(makeUrl`company/${'id'}/sync_indexes`, (x) => x),
  setGuidSignings: rcInstance.post(makeUrl`preferences/${'type'}/${'id'}/signing/set_guid`, c.validateSetSigningsGuid),
  getSigningsCopy: rcInstance.get(makeUrl`preferences/company/${'id'}/signing/copy`, c.validateGetSignings),
  deleteAllSignings: rcInstance.delete(makeUrl`preferences/${'type'}/${'id'}/signing/all`, c.validateRemoveSignings),
  getOrgFeatures: rcInstance.get(makeUrl`preferences/org/${'id'}/feature_set`, c.validateOrganizationsFeatures),
  setOrgFeatures: rcInstance.post(makeUrl`preferences/org/${'id'}/feature_set`, c.validateOrganizationsFeatures),
  getOrgBackupConnections: rcInstance.get(
    makeUrl`preferences/org/${'id'}/cloud_backup_connections`,
    c.validateOrgBackupConnections,
  ),
  getOrgFeaturesLayout: rcInstance.get(makeUrl`preferences/org/${'id'}/layout/features`, (x) => x),
  getCompanyUserPreferences: rcInstance.get(makeUrl`preferences/company-user`, (x) => x),
  getBackupSSOParams: rcInstance.get(makeUrl`cloudBackup/getSSOParams`, (x) => x),
  disconnectOrgBackup: rcInstance.get(makeUrl`cloudBackup/disconnect`, (x) => x),
  getCompanyUsers: rcInstance.get(makeUrl`company/${'id'}/users`, c.validateUsers),
  createCompanyUser: rcInstance.post(makeUrl`company/${'id'}/users`, c.validateUser),
  getOrgUsers: rcInstance.get(makeUrl`org/${'id'}/users`, c.validateUsers),
  downloadUsersReport: rcInstance.url('organizations/usersReport'),
  createOrgUser: rcInstance.post(makeUrl`org/${'id'}/users`, c.validateUser),
  updateOrgUser: rcInstance.post(makeUrl`users/${'id'}`, c.validateUser),
  uploadUserPicture: rcInstance.post(makeUrl`users/${'id'}/uploadPicture`, (x) => x),
  getCompany: rcInstance.get(makeUrl`company/${'id'}`, c.validateCompany),
  updateCompanyLogo: rcInstance.post(makeUrl`company/${'id'}/uploadLogo`, c.validateUpdateCompanyLogo),
  updateCompany: rcInstance.post(makeUrl`company/${'id'}`, c.validateUpdateCompany),
  assignOrgUser: rcInstance.post(makeUrl`users/${'id'}/company_assign`, c.validateAssignOrgUser),
  revokeOrgUser: rcInstance.delete(makeUrl`users/${'id'}/company_assign`, c.validateRevokeOrgUser),
  bulkAssignOrgUsers: rcInstance.post(makeUrl`company/${'companyId'}/bulk_user_assign`, (x) => x),
  bulkRevokeOrgUsers: rcInstance.delete(makeUrl`company/${'companyId'}/bulk_user_assign`, (x) => x),
  bulkAssignOrgCompanies: rcInstance.post(makeUrl`users/${'userId'}/bulk_company_assign`, (x) => x),
  bulkRevokeOrgCompanies: rcInstance.delete(makeUrl`users/${'userId'}/bulk_company_assign`, (x) => x),
  revokeUserFromAllCompanies: rcInstance.delete(
    makeUrl`users/${'id'}/company_assign/revoke_all`,
    c.validateRevokeOrgUser,
  ),
  deleteCompany: rcInstance.delete(makeUrl`company/${'id'}`, c.validateDeleteCompany),
  deleteOrgUser: rcInstance.delete(makeUrl`users/${'id'}`, c.validateDeleteUser),
  getCompaniesByErpType: rcInstance.get(makeUrl`getCompaniesByErpType`, c.validateCompanies),
  getCurrentERPSettings: rcInstance.get(
    makeUrl`preferences/company/${'id'}/erp_config`,
    c.validateERPSettingsContracts,
  ),
  setCurrentERPSettings: rcInstance.post(
    makeUrl`preferences/company/${'id'}/erp_config`,
    c.validateERPSettingsContracts,
  ),
  duplicateDocument: rcInstance.post('duplicateDocument', c.validateDuplicateDocument),
  getDocumentUrl: rcInstance.url('getDocument'),
  getDocumentLinkedCount: rcInstance.get('getCountOfDocumentByLinkpanel', (x) => x),
  getDocumentCsv: rcInstance.url('exportBankStatement'),
  getDocumentThumbnailUrl: rcInstance.url('getDocumentThumbnail'),
  getDocuments: rcInstance.post('getDocuments', c.validateGetDocuments),
  getUnreadRequests: rcInstance.get('GetUnreadRequests', c.validateUnreadRequests),
  removeDocuments: rcInstance.post('removeDocuments', c.validateRemoveDocuments),
  reprocessDocuments: rcInstance.post('documents/reprocess', (x) => x),
  stopDocumentProcessing: rcInstance.post('setDocumentAsViewed', c.validateDocumentMetadata),
  headerTablesCorrection: rcInstance.post('headerTablesCorrection', (x) => x),
  getEvents: rcInstance.get('getEvents', c.validateGetEvents),
  getEventFilters: rcInstance.get('getEventFilters', c.validateGetEventFilters),
  getCountOfNewEvents: rcInstance.get('getCountOfNewEvents', c.validateGetCountOfNewEvents),
  exportEventsHistory: rcInstance.url('exportEventsHistory'),
  getCurrencies: rcInstance.get('getCurrencies', c.validateCurrencies),
  getTimezones: rcInstance.get('getTimezones', (x) => x),
  activateErp: rcInstance.get('activateErp', c.validateActivateErp),
  importData: rcInstance.post('importData', c.validateERPSettingsContracts),
  getReconcileStatistic: rcInstance.post('getReconcileStatistic', c.validateReconcileStatistic),
  getReconciliationSearchParams: request.get('getSearchJournalEntry'),
  updateReconciliationSearchParams: request.post('getSearchJournalEntry'),
  reconcileTransaction: request.post('acceptSearchResult'),
  undoTransaction: request.post('undoFromSearch'),
  anGetDocument: rcInstance.url('anonymous/getDocument'),
  anGetDocumentMessagingHistory: rcInstance.get('anonymous/getDocumentMessagingHistory', (x) => x),
  anUpdateRequestTransactionAnonymously: rcInstance.post('anonymous/updateRequestTransactionAnonymously', (x) => x),
  getDocumentMessagingHistory: rcInstance.get('StatementDetailsRequest', (x) => x),
  createTransactionRequest: rcInstance.post('StatementDetailsRequest', (x) => x),
  markStatementRequestAsRead: rcInstance.post('MarkStatementRequestAsRead', (x) => x),
  getOrganizationList: rcInstance.get('organizations', (x) => x),
  // updateApproval: rcInstance.mock(baseApprovalMock, x => x),
  updateApproval: rcInstance.post('approvals', (x) => x),
  signApproval: rcInstance.post('approvals/document/sign', c.validateDocumentMetadata),
  rejectApproval: rcInstance.post('approvals/document/reject', (x) => x),
  // getApprovalRejectReasons: rcInstance.mock(approvalRejectReasons, x => x),
  getApprovalApproveReasons: request.get('approvals/getApprovalForm', (x) => x),
  updateApprovalApproveReasons: request.put('approvals/updateApprovalForm', (x) => x),
  changeBulkApprovalFlowStatus: request.post('bulkSetStatusApprovalFlow', (x) => x),
  bulkApprovalReplace: request.post('approvals/document/nodes/bulk_replace', (x) => x),
  getCategories: rcInstance.get('getWorkspaceCategories', (v) => v),
  moveDocumentToCategory: rcInstance.post('changeCategoryForDocuments', (x) => x),
  moveAllCategoryDocumentsToOtherCategory: rcInstance.get('moveDocumentsToCategory', (x) => x),
  purgeDocuments: rcInstance.post('documents/purge', (x) => x),
  purgeAllDocuments: rcInstance.post('documents/purge/all', (x) => x),
  restoreDocuments: rcInstance.post('documents/restore', (x) => x),
  setDocumentAsCover: rcInstance.post('setDocumentAsCover', (x) => x),
  gridDocumentsExport: rcInstance.post('grid/documents/export', (x) => x),
  getWorkspaceGridRows: rcInstance.post('grid/documents/search', (x) => x),
  getWorkspaceGridHeaders: rcInstance.get('grid/documents/headers', (x) => x),
  getInsightsGridHeaders: rcInstance.get('grid/insights/headers', (x) => x),
  getInsightsGridRows: rcInstance.post('grid/insights/search', (x) => x),
  getDocumentPaymentsGridHeaders: rcInstance.get('grid/payments/headers', (x) => x),
  getDocumentPaymentsGridRows: rcInstance.post('grid/payments/search', (x) => x),
  getInsightsGridFilterListByField: rcInstance.post('grid/insights/autocomplete', (x) => x),
  getDocumentsGridFilterListByField: rcInstance.post('grid/documents/autocomplete', (x) => x),
  getPresets: rcInstance.get('configs', (x) => x),
  createPreset: rcInstance.post('configs', (x) => x),
  updatePreset: rcInstance.put('configs', (x) => x),
  deletePreset: rcInstance.delete('configs', (x) => x),
  getPreset: rcInstance.get(makeUrl`configs/${'id'}`, (x) => x),
  getDashboardVendorExpensesHeaders: rcInstance.get('ag-grid/expenses/tree/headers', (x) => x),
  getDashboardVendorExpensesRows: rcInstance.post('ag-grid/expenses/tree/search', (x) => x),
  getDashboardVendorExpensesFilterOptions: rcInstance.post('ag-grid/expenses/autocomplete', (x) => x),
  getVendorBasedApprovalsRows: rcInstance.post('grid/vendor-preferences/approval-flow/search', (x) => x),
  getVendorBasedApprovalsHeaders: rcInstance.get('grid/vendor-preferences/approval-flow/headers', (x) => x),
  getVendorBasedApprovalsFilterListByField: rcInstance.post(
    'grid/vendor-preferences/approval-flow/autocomplete',
    (x) => x,
  ),
  getPolicyManagementGridRows: rcInstance.post('grid/vendor-preferences/policy/search', (x) => x),
  getPolicyManagementGridHeaders: rcInstance.get('grid/vendor-preferences/policy/headers', (x) => x),
  updatePolicyManagementGridData: rcInstance.post('vendor-preferences/policy', (x) => x),
  updateVendorBasedApprovals: rcInstance.post('vendor-preferences/approval-flow', (x) => x),
  resetVendorBasedApprovalsDefaults: rcInstance.delete('vendor-preferences/approval-flow/default-settings', (x) => x),
  getVendorBasedApprovalsDefaults: rcInstance.get('vendor-preferences/approval-flow/default-settings', (x) => x),
  updateVendorBasedApprovalsDefaults: rcInstance.post('vendor-preferences/approval-flow/default-settings', (x) => x),
  getVendorBasedApprovalsAdditionalEditors: rcInstance.get('vendor-preferences/approval-flow/users', (x) => x),
  addApproval: rcInstance.post('approvals/document/nodes/add', (x) => x),
  removeApproval: rcInstance.post('approvals/document/nodes/delete', (x) => x),
  addAdvancedApprovalGroup: rcInstance.post(makeUrl`approvals/document/advanced-group/add`, (x) => x),
  removeAdvancedApprovalGroup: rcInstance.post(makeUrl`approvals/document/advanced-group/delete`, (x) => x),
  changeOrderApproval: rcInstance.post('approvals/document/nodes/rearrange', (x) => x),
  setApprovalFlowStatus: rcInstance.post('approvals/document/flow/set_status', (x) => x),
  getEmailData: rcInstance.get('getEmailData', (x) => x),
  getNotifications: rcInstance.get(makeUrl`user/${'userGUID'}/notifications`, c.validateNotifications),
  updateNotifications: rcInstance.post(makeUrl`user/${'userGUID'}/notifications`, c.validateNotifications),
  getDocumentHotkeys: rcInstance.get('hotkeys', c.validateDocumentHotkeyList),
  updateUserFeatures: rcInstance.post('preferences/user/feature_set', c.validateUserFeatures),
  jeGetIndexForm: rcInstance.get('indexForm', (x) => x),
  getDocumentsDownloadURL: rcInstance.post('getDocumentsDownloadURL', (x) => x),
  getSuppliersDefaults: rcInstance.get('supplierFormGrid', (x) => x),
  updateSuppliersDefaults: rcInstance.post('supplierFormGrid', (x) => x),
  importCorruptedVendorPreferences: rcInstance.url('vendorPreferencesImportErrorsReport', (x) => x),
  importVendorPreferences: rcInstance.post('vendorPreferencesFile', (x) => x),
  exportVendorPreferences: rcInstance.url('vendorPreferencesFile', (x) => x),
  exportVendorPreferencesNew: rcInstance.url('vendor-preferences/export', (x) => x),
  importCorruptedVendorPreferencesNew: rcInstance.url('vendor-preferences/errors-report', (x) => x),
  importVendorPreferencesBackground: rcInstance.post('vendor-preferences/background-import', (x) => x),
  ignoreDocumentWarnings: rcInstance.post('ignoreWarningDocument', (x) => x),
  getAliases: rcInstance.get('supplier_view/aliases', (x) => x),
  updateAliases: rcInstance.post('supplier_view/aliases', (x) => x),
  getDocumentVendor: rcInstance.get('getDocumentVendor', c.validateInsightsDocumentVendor),
  sendDocByEmail: rcInstance.post('send_document_email', (x) => x),
  getEmailsForAutocomplete: rcInstance.get('email_autocomplete', (x) => x),
  // ws
  getWSConnectionUrl: rcInstance.post('notifications/connect', (x) => x),
  // approvals
  getApprovalGroups: rcInstance.get('approvals/basic-groups', (x) => x),
  createApprovalsGroup: rcInstance.post('approvals/basic-groups', (x) => x),
  updateApprovalGroup: rcInstance.post(makeUrl`approvals/basic-groups/${'groupId'}`, (x) => x),
  deleteApprovalsGroup: rcInstance.delete(makeUrl`approvals/basic-groups/${'groupId'}`, (x) => x),
  getApprovers: rcInstance.get('approvals/approvers', (x) => x),
  getAllApprovalUsers: rcInstance.get('approvals/users', (x) => x),
  createApprover: rcInstance.post('approvals/approvers', (x) => x),
  deleteApprover: rcInstance.delete(makeUrl`approvals/approvers/${'approverId'}`, (x) => x),
  getAdvancedApprovalsGroups: rcInstance.get('approvals/advanced-groups', (x) => x),
  createAdvancedApprovalsGroup: rcInstance.post('approvals/advanced-groups', (x) => x),
  updateAdvancedApprovalGroup: rcInstance.put(makeUrl`approvals/advanced-groups/${'id'}`, (x) => x),
  deleteAdvancedApprovalsGroup: rcInstance.delete(makeUrl`approvals/advanced-groups/${'id'}`, (x) => x),
  approvalsNodesReplace: rcInstance.post(makeUrl`approvals/document/nodes/replace-all`, (x) => x),
  // textract
  getExtractedTableData: rcInstance.get(makeUrl`tables_data/${'documentID'}`, (x) => x),
  getExtractedTableFieldsMapping: rcInstance.get(makeUrl`tables_data/fields/${'documentID'}`, (x) => x),
  learnExtractedTableFields: rcInstance.post(makeUrl`tables_data/learn_items_headers/${'documentID'}`, (x) => x),
  extractLines: rcInstance.post(makeUrl`tables_data/lines_extraction/${'documentID'}`, (x) => x),
  // organization api keys
  getOrganizationApiKeys: rcInstance.get('organizationApiKey', (x) => x),
  createOrganizationApiKey: rcInstance.post('organizationApiKey', (x) => x),
  updateOrganizationApiKey: rcInstance.put(makeUrl`organizationApiKey/${'apiKey'}`, (x) => x),
  deleteOrganizationApiKey: rcInstance.delete(makeUrl`organizationApiKey/${'apiKey'}`, (x) => x),
  // get user IP address
  getUserIP: rcInstance.get('getUserIP', (x) => x),
  notifyOfAppActivity: rcInstance.post('userActivity', (x) => x),
  requestAccessToModule: rcInstance.post('createTicket', (x) => x),
  pollingDeferredTask: rcInstance.get(makeUrl`tasks/${'taskId'}`, (x) => x),

  getMyPermissionsForPayModule: rcPaymentInstance.get('me/permissions', (x) => x),
  getPaymentProviders: rcPaymentInstance.get('payment-accounts', (x) => x),
  connectPaymentProvider: rcPaymentInstance.post('payment-accounts', (x) => x),
  changeStatusForPaymentProvider: rcPaymentInstance.post(makeUrl`payment-accounts/${'id'}/status-transition`, (x) => x),
  getPaymentProvider: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}`, (x) => x),
  getWalletBalances: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/funds/wallets`, (x) => x),
  requestAccessToProvider: rcPaymentInstance.post(makeUrl`payment-accounts/${'id'}/request-access`, (x) => x),
  disconnectPaymentProvider: rcPaymentInstance.delete(makeUrl`payment-accounts/${'id'}`, (x) => x),
  getProviderPayingAccounts: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/funds/paying-accounts`, (x) => x),
  getBankAccounts: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/bank-accounts`, (x) => x),
  getPayingProviders: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/funds/paying-providers`, (x) => x),
  createProviderPayingAccounts: rcPaymentInstance.post(
    makeUrl`payment-accounts/${'id'}/funds/paying-accounts`,
    (x) => x,
  ),
  updateProviderPayingAccounts: rcPaymentInstance.patch(
    makeUrl`payment-accounts/${'id'}/funds/paying-accounts/${'payingAccountId'}`,
    (x) => x,
  ),
  disconnectProviderPayingAccounts: rcPaymentInstance.delete(
    makeUrl`payment-accounts/${'id'}/funds/paying-accounts/${'payingAccountId'}`,
    (x) => x,
  ),
  getProviderUsers: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/users`, (x) => x),
  addProviderUsers: rcPaymentInstance.post(makeUrl`payment-accounts/${'id'}/users/groups`, (x) => x),
  deleteProviderUsers: rcPaymentInstance.delete(makeUrl`payment-accounts/${'id'}/users/groups`, (x) => x),
  getProviderApprovers: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/approvers`, (x) => x),
  getProviderApprovalTemplate: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/approval-template`, (x) => x),
  createProviderApprovalTemplate: rcPaymentInstance.post(makeUrl`payment-accounts/${'id'}/approval-template`, (x) => x),
  updateProviderApprovalTemplate: rcPaymentInstance.put(makeUrl`payment-accounts/${'id'}/approval-template`, (x) => x),
  deleteProviderApprovalTemplate: rcPaymentInstance.delete(
    makeUrl`payment-accounts/${'id'}/approval-template`,
    (x) => x,
  ),
  deleteBeneficiary: rcPaymentInstance.delete(makeUrl`beneficiaries/${'id'}`, (x) => x),
  getProviderSettings: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/settings`, (x) => x),
  updateProviderSettings: rcPaymentInstance.put(makeUrl`payment-accounts/${'id'}/settings`, (x) => x),

  getPayCategories: rcPaymentInstance.get(makeUrl`payment-accounts/${'id'}/workspace-categories`, (v) => v),
  getPaymentsGridHeaders: rcPaymentInstance.get('grid/payments/headers', (x) => x),
  getPaymentsGridRows: rcPaymentInstance.post('grid/payments/search', (x) => x),
  getPaymentsGridTotalByCategory: rcPaymentInstance.get('grid/payments/total', (x) => x),
  getPaymentsGridTotalByState: rcPaymentInstance.get('grid/payments/total-by-state', (x) => x),
  getPaymentsGridFilterListByField: rcPaymentInstance.post('grid/payments/autocomplete', (x) => x),
  getPaymentThumbnailUrl: rcPaymentInstance.url('grid/payments/thumbnail'),
  bulkMergePayments: rcPaymentInstance.post('payments/merge', (x) => x),
  bulkSendToApprovalPayments: rcPaymentInstance.post('payments/send-to-approval', (x) => x),
  bulkSaveForLaterPayments: rcPaymentInstance.post('payments/save-for-later', (x) => x),
  bulkArchivePayments: rcPaymentInstance.post('payments/archive', (x) => x),
  bulkApprovePayments: rcPaymentInstance.post('payments/approve', (x) => x),
  bulkResetApprovalFlowPayments: rcPaymentInstance.post('payments/reset-approvals', (x) => x),
  bulkPayPayments: rcPaymentInstance.post('payments/pay', (x) => x),
  bulkDeletePayments: rcPaymentInstance.post('payments/delete', (x) => x),

  getPaymentUrl: rcPaymentInstance.url('payments/preview'),
  getPayment: rcPaymentInstance.get(makeUrl`payments/${'id'}`, (x) => x),
  updatePayment: rcPaymentInstance.patch(makeUrl`payments/${'id'}`, (x) => x),
  deletePayment: rcPaymentInstance.delete(makeUrl`payments/${'id'}`, (x) => x),

  startPaymentApprovalFlow: rcPaymentInstance.post(makeUrl`payments/${'id'}/approvals/start`, (x) => x),
  resetPaymentProcess: rcPaymentInstance.post(makeUrl`payments/${'id'}/approvals/reset`, (x) => x),
  approvePayment: rcPaymentInstance.post(makeUrl`payments/${'id'}/approvals/approve`, (x) => x),
  rejectPayment: rcPaymentInstance.post(makeUrl`payments/${'id'}/approvals/reject`, (x) => x),
  payPayment: rcPaymentInstance.post(makeUrl`payments/${'id'}/pay`, (x) => x),
  voidPayment: rcPaymentInstance.post(makeUrl`payments/${'id'}/void`, (x) => x),
  recreatePayment: rcPaymentInstance.post(makeUrl`payments/${'id'}/recreate`, (x) => x),
  unmergePayment: rcPaymentInstance.post(makeUrl`payments/${'id'}/unmerge`, (x) => x),
  updatePaymentTag: rcPaymentInstance.post(makeUrl`payments/${'id'}/tagify`, (x) => x),
  getPreviousPaymentsGridHeaders: rcPaymentInstance.get('grid/previous-payments/headers', (x) => x),
  getPreviousPaymentsGridRows: rcPaymentInstance.post('grid/previous-payments/search', (x) => x),
  getPreviousPaymentsGridTotalByCategory: rcPaymentInstance.get('grid/previous-payments/total', (x) => x),
  getPreviousPaymentsGridFilterListByField: rcPaymentInstance.post('grid/previous-payments/autocomplete', (x) => x),
};
